import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction } from 'react-router-dom';

import { employeeService } from '../../admin/services/employeeService';
import {
  DEFAULT_CONVERSATION_LIST_PAGE_SIZE,
  createConversationCustomList,
  deleteConversationCustomList,
  getConversationsPage,
  GetConversationsOptions,
  listConversationCustomLists,
  moveConversationToCustomList,
  moveConversationsToCustomList,
  updateConversationCustomList,
  updateConversationAssignment,
  updateConversationPreferences,
} from '../services/conversationService';
import { Conversation, ConversationCustomList } from '../types/Conversation';
import { FilterAiMuted, FilterChannel, FilterStatus, FilterWhatsAppThread, NavSection, applyNavSectionToSearchParams, getConversationKindForNavSection, getCustomListIdFromNavSection } from '../types/conversationNav';
import { idsEqual } from '../utils/messageUtils';

interface UseConversationListStateOptions {
  navSection: NavSection;
  setNavSection: (section: NavSection) => void;
  filterStatus: FilterStatus;
  filterAiMuted: FilterAiMuted;
  filterChannel: FilterChannel;
  filterWhatsAppThread: FilterWhatsAppThread;
  filterDate: string;
  showCommentThreads: boolean;
  selectedId: string | null;
  navigate: NavigateFunction;
  setSelectedConversationId: (conversationId: string | null) => void;
  setIsChatOpen: (value: boolean) => void;
  setSelectedConv: React.Dispatch<React.SetStateAction<Conversation | null>>;
  setDetailsError: (value: string | null) => void;
  setOpenRowMenuId: (value: string | null) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface UseConversationListStateResult {
  list: Conversation[];
  hiddenList: Conversation[];
  customLists: ConversationCustomList[];
  customListConversations: Conversation[];
  loadingList: boolean;
  loadingMoreList: boolean;
  listHasNext: boolean;
  hiddenListHasNext: boolean;
  customListHasNext: boolean;
  listTotal: number;
  hiddenListTotal: number;
  customListTotal: number;
  listError: string | null;
  setList: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setHiddenList: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setCustomListConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  loadConversationLists: () => Promise<void>;
  loadMoreConversationList: () => Promise<void>;
  refreshCustomLists: () => Promise<void>;
  createCustomList: (name: string) => Promise<ConversationCustomList | null>;
  updateCustomList: (listId: string, name: string) => Promise<boolean>;
  deleteCustomList: (listId: string) => Promise<boolean>;
  moveConversationToList: (conversationId: string, customListId: string | null) => Promise<void>;
  moveConversationsToList: (conversationIds: string[], customListId: string | null) => Promise<void>;
  mergeCreatedConversationIntoList: (conversation: Conversation) => void;
  clearSelectionForConversation: (conversationId: string) => void;
  toggleFavoriteConversation: (conversationId: string, currentFavorite: boolean) => Promise<void>;
  togglePinnedConversation: (conversationId: string, currentPinned: boolean) => Promise<void>;
  hideConversationForMe: (conversationId: string) => Promise<void>;
  restoreConversationForMe: (conversationId: string) => Promise<void>;
  employeeOptions: Array<{ id: string; name: string }>;
  onAssignConversation: (conversationId: string, employeeId: string) => Promise<void>;
  onUnassignConversation: (conversationId: string) => Promise<void>;
}

export const useConversationListState = ({
  navSection,
  setNavSection,
  filterStatus,
  filterAiMuted,
  filterChannel,
  filterWhatsAppThread,
  filterDate,
  showCommentThreads,
  selectedId,
  navigate,
  setSelectedConversationId,
  setIsChatOpen,
  setSelectedConv,
  setDetailsError,
  setOpenRowMenuId,
  showToast,
}: UseConversationListStateOptions): UseConversationListStateResult => {
  const { t } = useTranslation('conversations');
  const [list, setList] = useState<Conversation[]>([]);
  const [hiddenList, setHiddenList] = useState<Conversation[]>([]);
  const [customLists, setCustomLists] = useState<ConversationCustomList[]>([]);
  const [customListConversations, setCustomListConversations] = useState<Conversation[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMoreList, setLoadingMoreList] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [hiddenListPage, setHiddenListPage] = useState(1);
  const [customListPage, setCustomListPage] = useState(1);
  const [listHasNext, setListHasNext] = useState(false);
  const [hiddenListHasNext, setHiddenListHasNext] = useState(false);
  const [customListHasNext, setCustomListHasNext] = useState(false);
  const [listTotal, setListTotal] = useState(0);
  const [hiddenListTotal, setHiddenListTotal] = useState(0);
  const [customListTotal, setCustomListTotal] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ id: string; name: string }>>([]);

  const activeCustomListId = getCustomListIdFromNavSection(navSection);

  const buildListQueryOptions = useCallback((spamOnly: boolean, customListId?: string | null): GetConversationsOptions => {
    const whatsAppThreadKind: GetConversationsOptions['conversationKind'] = filterWhatsAppThread === 'groups'
      ? 'whatsapp-group'
      : filterWhatsAppThread === 'direct'
        ? 'whatsapp-direct'
        : undefined;
    const channel = filterWhatsAppThread === 'all' ? filterChannel : 'whatsApp';
    const conversationKind = spamOnly || customListId
      ? undefined
      : (whatsAppThreadKind ?? getConversationKindForNavSection(navSection));

    return {
      spamOnly,
      customListId: customListId ?? undefined,
      conversationKind,
      status: filterStatus,
      aiMuted: filterAiMuted,
      channel,
      date: filterDate,
      privateMessagesOnly: !showCommentThreads,
      sortBy: 'lastActivity',
      sortDir: 'desc',
      pageSize: DEFAULT_CONVERSATION_LIST_PAGE_SIZE,
    };
  }, [filterAiMuted, filterChannel, filterDate, filterStatus, filterWhatsAppThread, navSection, showCommentThreads]);

  const appendUniqueConversations = useCallback((current: Conversation[], incoming: Conversation[]) => {
    if (incoming.length === 0) {
      return current;
    }

    const existingIds = new Set(current.map(item => item.id));
    const nextItems = incoming.filter(item => !existingIds.has(item.id));
    return nextItems.length > 0 ? [...current, ...nextItems] : current;
  }, []);

  useEffect(() => {
    void employeeService.getEmployees({ isActive: true, sortBy: 'name', sortDir: 'asc', pageSize: 200 })
      .then((employees) => {
        setEmployeeOptions(employees.map((employee) => ({
          id: employee.id,
          name: employee.name || employee.email,
        })));
      })
      .catch(() => {
        setEmployeeOptions([]);
      });
  }, []);

  const upsertConversation = useCallback((items: Conversation[], conversation: Conversation) => {
    const existingIndex = items.findIndex(item => idsEqual(item.id, conversation.id));
    if (existingIndex === -1) {
      return [...items, conversation];
    }

    const existing = items[existingIndex];
    const next = [...items];
    next[existingIndex] = {
      ...existing,
      ...conversation,
      messages: conversation.messages.length > 0 ? conversation.messages : existing.messages,
      participants: conversation.participants.length > 0 ? conversation.participants : existing.participants,
    };
    return next;
  }, []);

  const refreshCustomLists = useCallback(async () => {
    try {
      const lists = await listConversationCustomLists();
      setCustomLists(lists);
    } catch {
      setCustomLists([]);
    }
  }, []);

  const loadCustomListConversations = useCallback(async (customListId: string, page = 1, append = false) => {
    const pageResult = await getConversationsPage({
      ...buildListQueryOptions(false, customListId),
      page,
    });

    setCustomListConversations(current => append ? appendUniqueConversations(current, pageResult.items) : pageResult.items);
    setCustomListPage(pageResult.page);
    setCustomListHasNext(pageResult.hasNext);
    setCustomListTotal(pageResult.total);
  }, [appendUniqueConversations, buildListQueryOptions]);

  const loadConversationLists = useCallback(async () => {
    setLoadingList(true);
    try {
      const [visibleConversations, spamConversations, lists] = await Promise.all([
        getConversationsPage({
          ...buildListQueryOptions(false),
          page: 1,
        }),
        getConversationsPage({
          ...buildListQueryOptions(true),
          page: 1,
        }),
        listConversationCustomLists(),
      ]);

      setList(visibleConversations.items);
      setListPage(visibleConversations.page);
      setListHasNext(visibleConversations.hasNext);
      setListTotal(visibleConversations.total);
      setHiddenList(spamConversations.items);
      setHiddenListPage(spamConversations.page);
      setHiddenListHasNext(spamConversations.hasNext);
      setHiddenListTotal(spamConversations.total);
      setCustomLists(lists);
      setListError(null);

      if (activeCustomListId) {
        await loadCustomListConversations(activeCustomListId, 1, false);
      } else {
        setCustomListConversations([]);
        setCustomListPage(1);
        setCustomListHasNext(false);
        setCustomListTotal(0);
      }
    } catch {
      setListError(t('failedToLoadConversations'));
    } finally {
      setLoadingList(false);
    }
  }, [activeCustomListId, buildListQueryOptions, loadCustomListConversations, t]);

  const loadMoreConversationList = useCallback(async () => {
    const isSpamView = navSection === 'spam';
    const isCustomListView = Boolean(activeCustomListId);
    const hasNext = isSpamView ? hiddenListHasNext : isCustomListView ? customListHasNext : listHasNext;
    if (!hasNext || loadingList || loadingMoreList) {
      return;
    }

    const nextPage = (isSpamView ? hiddenListPage : isCustomListView ? customListPage : listPage) + 1;
    setLoadingMoreList(true);
    try {
      const page = await getConversationsPage({
        ...buildListQueryOptions(isSpamView, isCustomListView ? activeCustomListId : undefined),
        page: nextPage,
      });

      if (isSpamView) {
        setHiddenList(current => appendUniqueConversations(current, page.items));
        setHiddenListPage(page.page);
        setHiddenListHasNext(page.hasNext);
        setHiddenListTotal(page.total);
      } else if (isCustomListView && activeCustomListId) {
        setCustomListConversations(current => appendUniqueConversations(current, page.items));
        setCustomListPage(page.page);
        setCustomListHasNext(page.hasNext);
        setCustomListTotal(page.total);
      } else {
        setList(current => appendUniqueConversations(current, page.items));
        setListPage(page.page);
        setListHasNext(page.hasNext);
        setListTotal(page.total);
      }
    } catch {
      showToast(t('failedToLoadConversations'), 'error');
    } finally {
      setLoadingMoreList(false);
    }
  }, [
    activeCustomListId,
    appendUniqueConversations,
    buildListQueryOptions,
    customListHasNext,
    customListPage,
    hiddenListHasNext,
    hiddenListPage,
    listHasNext,
    listPage,
    loadingList,
    loadingMoreList,
    navSection,
    showToast,
    t,
  ]);

  useEffect(() => {
    if (!activeCustomListId || loadingList) {
      return;
    }

    void loadCustomListConversations(activeCustomListId, 1, false).catch(() => {
      showToast(t('failedToLoadConversations'), 'error');
    });
  }, [activeCustomListId, filterAiMuted, filterChannel, filterDate, filterStatus, filterWhatsAppThread, loadCustomListConversations, loadingList, showToast, t]);

  useEffect(() => {
    void loadConversationLists();
  }, [loadConversationLists]);

  const mergeCreatedConversationIntoList = useCallback((conversation: Conversation) => {
    setList(current => {
      const existingIndex = current.findIndex(item => item.id === conversation.id);
      if (existingIndex === -1) {
        return [...current, conversation];
      }

      const existing = current[existingIndex];
      const merged = {
        ...existing,
        ...conversation,
        messages: conversation.messages.length > 0 ? conversation.messages : existing.messages,
      };

      const next = [...current];
      next[existingIndex] = merged;
      return next;
    });
  }, []);

  const patchConversationPreferenceInState = useCallback((conversationId: string, patch: Partial<Conversation>) => {
    setList(prev => prev.map(c => c.id === conversationId ? { ...c, ...patch } : c));
    setHiddenList(prev => prev.map(c => c.id === conversationId ? { ...c, ...patch } : c));
    setCustomListConversations(prev => prev.map(c => c.id === conversationId ? { ...c, ...patch } : c));
    setSelectedConv(prev => prev && prev.id === conversationId ? { ...prev, ...patch } : prev);
  }, [setSelectedConv]);

  const clearSelectionForConversation = useCallback((conversationId: string) => {
    if (!selectedId || !idsEqual(selectedId, conversationId)) {
      return;
    }

    setSelectedConv(null);
    setDetailsError(null);
    setSelectedConversationId(null);
    setIsChatOpen(false);
    const params = new URLSearchParams(window.location.search);
    applyNavSectionToSearchParams(params, navSection);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    navigate(`/conversations${suffix}`);
  }, [navigate, navSection, selectedId, setDetailsError, setIsChatOpen, setSelectedConv, setSelectedConversationId]);

  const applyConversationSnapshotInState = useCallback((conversation: Conversation) => {
    patchConversationPreferenceInState(conversation.id, {
      isFavorite: conversation.isFavorite,
      isPinned: conversation.isPinned,
      isSpam: conversation.isSpam,
      isHidden: conversation.isHidden,
      note: conversation.note,
      notes: conversation.notes,
      visibleNoteCount: conversation.visibleNoteCount,
      assignedToEmployeeId: conversation.assignedToEmployeeId,
      assignedToEmployeeName: conversation.assignedToEmployeeName,
      updatedAt: conversation.updatedAt,
    });
  }, [patchConversationPreferenceInState]);

  const moveConversationToSpamState = useCallback((conversation: Conversation) => {
    setList(prev => prev.filter(item => !idsEqual(item.id, conversation.id)));
    setHiddenList(prev => upsertConversation(prev, { ...conversation, isSpam: true, isHidden: true }));
    clearSelectionForConversation(conversation.id);
  }, [clearSelectionForConversation, upsertConversation]);

  const moveConversationToVisibleState = useCallback((conversation: Conversation) => {
    setHiddenList(prev => prev.filter(item => !idsEqual(item.id, conversation.id)));
    setList(prev => upsertConversation(prev, { ...conversation, isSpam: false, isHidden: false }));
    if (navSection === 'spam') {
      clearSelectionForConversation(conversation.id);
    }
  }, [clearSelectionForConversation, navSection, upsertConversation]);

  const toggleFavoriteConversation = useCallback(async (conversationId: string, currentFavorite: boolean) => {
    const nextFavorite = !currentFavorite;
    patchConversationPreferenceInState(conversationId, { isFavorite: nextFavorite });

    try {
      const updated = await updateConversationPreferences(conversationId, { isFavorite: nextFavorite });
      if (updated) {
        patchConversationPreferenceInState(conversationId, {
          isFavorite: updated.isFavorite,
          isPinned: updated.isPinned,
          isSpam: updated.isSpam,
          isHidden: updated.isHidden,
          note: updated.note,
          notes: updated.notes,
          visibleNoteCount: updated.visibleNoteCount,
          updatedAt: updated.updatedAt,
        });
      }
    } catch {
      patchConversationPreferenceInState(conversationId, { isFavorite: currentFavorite });
      showToast(t('failedToUpdateFavorite'), 'error');
    }

    setOpenRowMenuId(null);
  }, [patchConversationPreferenceInState, setOpenRowMenuId, showToast, t]);

  const togglePinnedConversation = useCallback(async (conversationId: string, currentPinned: boolean) => {
    const nextPinned = !currentPinned;
    patchConversationPreferenceInState(conversationId, { isPinned: nextPinned });

    try {
      const updated = await updateConversationPreferences(conversationId, { isPinned: nextPinned });
      if (updated) {
        patchConversationPreferenceInState(conversationId, {
          isFavorite: updated.isFavorite,
          isPinned: updated.isPinned,
          isSpam: updated.isSpam,
          isHidden: updated.isHidden,
          note: updated.note,
          notes: updated.notes,
          visibleNoteCount: updated.visibleNoteCount,
          updatedAt: updated.updatedAt,
        });
      }
    } catch {
      patchConversationPreferenceInState(conversationId, { isPinned: currentPinned });
      showToast(t('failedToUpdatePinState'), 'error');
    }

    setOpenRowMenuId(null);
  }, [patchConversationPreferenceInState, setOpenRowMenuId, showToast, t]);

  const markConversationAsSpam = useCallback(async (conversationId: string) => {
    const confirmed = window.confirm(t('markAsSpamConfirm'));
    if (!confirmed) {
      setOpenRowMenuId(null);
      return;
    }

    try {
      const updated = await updateConversationPreferences(conversationId, { isSpam: true });
      if (!updated?.isSpam) {
        throw new Error('Conversation was not marked as spam');
      }

      moveConversationToSpamState(updated);
      showToast(t('conversationMarkedAsSpam'), 'success');
    } catch {
      showToast(t('failedToMarkConversationAsSpam'), 'error');
    }

    setOpenRowMenuId(null);
  }, [moveConversationToSpamState, setOpenRowMenuId, showToast, t]);

  const unmarkConversationAsSpam = useCallback(async (conversationId: string) => {
    try {
      const updated = await updateConversationPreferences(conversationId, { isSpam: false });
      if (!updated || updated.isSpam) {
        throw new Error('Conversation was not removed from spam');
      }

      moveConversationToVisibleState(updated);
      showToast(t('conversationRemovedFromSpam'), 'success');
    } catch {
      showToast(t('failedToRemoveConversationFromSpam'), 'error');
    }

    setOpenRowMenuId(null);
  }, [moveConversationToVisibleState, setOpenRowMenuId, showToast, t]);

  const onAssignConversation = useCallback(async (conversationId: string, employeeId: string) => {
    const previous = list.find(c => c.id === conversationId);
    const selectedEmployee = employeeOptions.find(employee => employee.id === employeeId);

    patchConversationPreferenceInState(conversationId, {
      assignedToEmployeeId: '__pending__',
      assignedToEmployeeName: t('assigning'),
    });

    try {
      const updated = await updateConversationAssignment(conversationId, { assignedToEmployeeId: employeeId });
      if (updated) {
        applyConversationSnapshotInState(updated);
        showToast(t('conversationAssigned', {
          name: updated.assignedToEmployeeName || selectedEmployee?.name || '',
        }), 'success');
      }
    } catch {
      patchConversationPreferenceInState(conversationId, {
        assignedToEmployeeId: previous?.assignedToEmployeeId,
        assignedToEmployeeName: previous?.assignedToEmployeeName,
      });
      showToast(t('failedToAssignConversation'), 'error');
    }

    setOpenRowMenuId(null);
  }, [applyConversationSnapshotInState, employeeOptions, list, patchConversationPreferenceInState, setOpenRowMenuId, showToast, t]);

  const applyConversationCustomListState = useCallback((conversation: Conversation) => {
    const customListId = conversation.customListId ?? null;
    const previousCustomListId = list.find(item => idsEqual(item.id, conversation.id))?.customListId
      ?? hiddenList.find(item => idsEqual(item.id, conversation.id))?.customListId
      ?? customListConversations.find(item => idsEqual(item.id, conversation.id))?.customListId
      ?? null;

    patchConversationPreferenceInState(conversation.id, {
      customListId: conversation.customListId,
      customListName: conversation.customListName,
      updatedAt: conversation.updatedAt,
    });

    setList(current => current.map(item => (
      idsEqual(item.id, conversation.id)
        ? { ...item, customListId: conversation.customListId, customListName: conversation.customListName }
        : item
    )));
    setHiddenList(current => current.map(item => (
      idsEqual(item.id, conversation.id)
        ? { ...item, customListId: conversation.customListId, customListName: conversation.customListName }
        : item
    )));

    if (activeCustomListId && customListId === activeCustomListId) {
      setCustomListConversations(current => upsertConversation(current, conversation));
    } else if (activeCustomListId && (previousCustomListId === activeCustomListId || idsEqual(conversation.id, conversation.id))) {
      setCustomListConversations(current => current.filter(item => !idsEqual(item.id, conversation.id)));
      if (selectedId && idsEqual(selectedId, conversation.id) && customListId !== activeCustomListId) {
        clearSelectionForConversation(conversation.id);
      }
    }
  }, [activeCustomListId, clearSelectionForConversation, customListConversations, hiddenList, list, patchConversationPreferenceInState, selectedId, upsertConversation]);

  const createCustomList = useCallback(async (name: string) => {
    try {
      const created = await createConversationCustomList(name);
      await refreshCustomLists();
      showToast(t('customListCreated', { name: created.name }), 'success');
      return created;
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToCreateCustomList'), 'error');
      return null;
    }
  }, [refreshCustomLists, showToast, t]);

  const updateCustomList = useCallback(async (listId: string, name: string) => {
    try {
      const updated = await updateConversationCustomList(listId, name);
      await refreshCustomLists();
      setList(current => current.map(conversation => (
        conversation.customListId === listId
          ? { ...conversation, customListName: updated.name }
          : conversation
      )));
      setHiddenList(current => current.map(conversation => (
        conversation.customListId === listId
          ? { ...conversation, customListName: updated.name }
          : conversation
      )));
      setCustomListConversations(current => current.map(conversation => (
        conversation.customListId === listId
          ? { ...conversation, customListName: updated.name }
          : conversation
      )));
      setSelectedConv(current => (
        current?.customListId === listId
          ? { ...current, customListName: updated.name }
          : current
      ));
      showToast(t('customListUpdated', { name: updated.name }), 'success');
      return true;
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToUpdateCustomList'), 'error');
      return false;
    }
  }, [refreshCustomLists, setSelectedConv, showToast, t]);

  const deleteCustomList = useCallback(async (listId: string) => {
    try {
      await deleteConversationCustomList(listId);
      await refreshCustomLists();
      setList(current => current.map(conversation => (
        conversation.customListId === listId
          ? { ...conversation, customListId: undefined, customListName: undefined }
          : conversation
      )));
      setHiddenList(current => current.map(conversation => (
        conversation.customListId === listId
          ? { ...conversation, customListId: undefined, customListName: undefined }
          : conversation
      )));
      if (getCustomListIdFromNavSection(navSection) === listId) {
        setCustomListConversations([]);
        setNavSection('conversations');
        const params = new URLSearchParams(window.location.search);
        applyNavSectionToSearchParams(params, 'conversations');
        const suffix = params.toString() ? `?${params.toString()}` : '';
        navigate(`/conversations${suffix}`, { replace: true });
      }
      showToast(t('customListDeleted'), 'success');
      return true;
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDeleteCustomList'), 'error');
      return false;
    }
  }, [navSection, navigate, refreshCustomLists, setNavSection, showToast, t]);

  const moveConversationToList = useCallback(async (conversationId: string, customListId: string | null) => {
    try {
      const updated = await moveConversationToCustomList(conversationId, customListId);
      if (!updated) {
        throw new Error('Conversation was not updated');
      }

      applyConversationCustomListState(updated);
      await refreshCustomLists();
      showToast(
        customListId ? t('conversationMovedToCustomList', { name: updated.customListName ?? '' }) : t('conversationRemovedFromCustomList'),
        'success',
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToMoveConversationToCustomList'), 'error');
    }

    setOpenRowMenuId(null);
  }, [applyConversationCustomListState, refreshCustomLists, setOpenRowMenuId, showToast, t]);

  const moveConversationsToList = useCallback(async (conversationIds: string[], customListId: string | null) => {
    if (conversationIds.length === 0) {
      return;
    }

    try {
      const updatedConversations = await moveConversationsToCustomList(conversationIds, customListId);
      updatedConversations.forEach(applyConversationCustomListState);
      await refreshCustomLists();
      showToast(
        customListId ? t('conversationsMovedToCustomList') : t('conversationsRemovedFromCustomList'),
        'success',
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToMoveConversationToCustomList'), 'error');
    }
  }, [applyConversationCustomListState, refreshCustomLists, showToast, t]);

  const onUnassignConversation = useCallback(async (conversationId: string) => {
    const previous = list.find(c => c.id === conversationId);

    patchConversationPreferenceInState(conversationId, {
      assignedToEmployeeId: undefined,
      assignedToEmployeeName: undefined,
    });

    try {
      const updated = await updateConversationAssignment(conversationId, { assignedToEmployeeId: null });
      if (updated) {
        applyConversationSnapshotInState(updated);
        showToast(t('conversationUnassigned'), 'success');
      }
    } catch {
      patchConversationPreferenceInState(conversationId, {
        assignedToEmployeeId: previous?.assignedToEmployeeId,
        assignedToEmployeeName: previous?.assignedToEmployeeName,
      });
      showToast(t('failedToUnassignConversation'), 'error');
    }

    setOpenRowMenuId(null);
  }, [applyConversationSnapshotInState, list, patchConversationPreferenceInState, setOpenRowMenuId, showToast, t]);

  return {
    list,
    hiddenList,
    customLists,
    customListConversations,
    loadingList,
    loadingMoreList,
    listHasNext,
    hiddenListHasNext,
    customListHasNext,
    listTotal,
    hiddenListTotal,
    customListTotal,
    listError,
    setList,
    setHiddenList,
    setCustomListConversations,
    loadConversationLists,
    loadMoreConversationList,
    refreshCustomLists,
    createCustomList,
    updateCustomList,
    deleteCustomList,
    moveConversationToList,
    moveConversationsToList,
    mergeCreatedConversationIntoList,
    clearSelectionForConversation,
    toggleFavoriteConversation,
    togglePinnedConversation,
    hideConversationForMe: markConversationAsSpam,
    restoreConversationForMe: unmarkConversationAsSpam,
    employeeOptions,
    onAssignConversation,
    onUnassignConversation,
  };
};